import PropTypes from 'prop-types'
import ReactSelect from 'react-select'
import styled from 'styled-components'
import sortBy from 'lodash/sortBy'
import isEmpty from 'lodash/isEmpty'
import { useTranslation } from 'react-i18next'

import colors from 'components/utils/colors'
import Icon from 'components/Shared/Icon/Index'
import LoadingIndicator from 'components/Shared/Select/components/LoadingIndicator'
import NoOptionsMessage from 'components/Shared/Select/components/NoOptionsMessage'
import Option from 'components/Shared/Select/components/Option'
import DropdownIndicator from 'components/Shared/InlineSelect/components/DropdownIndicator'

import SelectControl from './components/SelectControl'
import ValueContainer from './components/ValueContainer'
import Menu from './components/Menu'
import ClearIndicator from './components/ClearIndicator'
import MultiValue from './components/MultiValue'
import MultiValueContainer from './components/MultiValueContainer'
import MultiValueLabel from './components/MultiValueLabel'
import MultiValueRemove from './components/MultiValueRemove'
import Input from './components/Input'
import SingleValue from './components/SingleValue'

const StyledWrapper = styled.div`
  font-size: 12px;
  display: flex;
  align-items: center;
`

const StyledFilterTitle = styled.label`
  font-weight: 600;
  color: ${colors.secondary20};
  padding-right: 16px;
  white-space: nowrap;
`

const StyledIcon = styled(Icon)`
  padding-right: 4px;
`

const FilterWithDropdown = ({
  title, options, isMulti, iconName, placeholder, maxDisplaySelectedItems,
  value, noAlphabetOrder, onChange, iconComponentsMap, dataTest, withBadges,
}) => {
  const { t } = useTranslation()

  const handleChange = (selectedItems) => {
    const newValue = selectedItems ? (isMulti ? selectedItems.map(i => i['value']) : selectedItems['value']) : []
    onChange(newValue)
  }

  const getMultiSelectedItems = (values) => {
    let selectedItems = values.map(val => {
      const option = options.find(e => e.value === val) || {}
      const newOption = {
        value: val,
        label: option.label || val,
      }
      return newOption
    })

    if (!noAlphabetOrder) {
      selectedItems = sortBy(selectedItems, ['label'])
    }

    return selectedItems
  }

  const getSelectedItems = () => {
    const selectedItems = isMulti ? getMultiSelectedItems(value) : options.find(e => e.value === value)
    return selectedItems || null
  }

  const components = {
    IndicatorSeparator: () => null,
    Control: SelectControl,
    ValueContainer: ValueContainer,
    Menu: Menu,
    DropdownIndicator: DropdownIndicator,
    LoadingIndicator: LoadingIndicator,
    ClearIndicator: ClearIndicator,
    MultiValue: MultiValue,
    MultiValueContainer: MultiValueContainer,
    MultiValueLabel: MultiValueLabel,
    MultiValueRemove: MultiValueRemove,
    NoOptionsMessage: NoOptionsMessage,
    Input: Input,
    SingleValue: SingleValue,
    Option: Option,
  }

  const hasValue = !isEmpty(value)
  const selectedValue = getSelectedItems()

  return (
    <StyledWrapper data-test={dataTest}>
      {title && <StyledFilterTitle data-test='filter-title'>{title}</StyledFilterTitle>}
      {iconName && <StyledIcon iconName={iconName} active={hasValue} />}
      <ReactSelect
        value={selectedValue}
        options={options}
        isMulti={isMulti}
        classNamePrefix='react-select'
        placeholder={placeholder}
        hideSelectedOptions={false}
        onChange={handleChange}
        maxMenuHeight={null}
        isClearable={hasValue}
        components={components}
        defaultMenuIsOpen={false} /* use it for debugging */
        noOptionsMessage={() => t('select.noOptions')}
        //
        // custom props
        iconComponentsMap={iconComponentsMap}
        maxDisplaySelectedItems={maxDisplaySelectedItems}
        withBadges={withBadges}
      />
    </StyledWrapper>
  )
}

FilterWithDropdown.propTypes = {
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.array.isRequired,
  ]),
  options: PropTypes.array.isRequired,
  isMulti: PropTypes.bool,
  iconName: PropTypes.string,
  placeholder: PropTypes.string,
  noAlphabetOrder: PropTypes.bool,
  maxDisplaySelectedItems: PropTypes.number,
  iconComponentsMap: PropTypes.object,
  dataTest: PropTypes.string,
  withBadges: PropTypes.bool,
}

export default FilterWithDropdown
